import React, { useState, useMemo } from 'react';
import { Modal, Box, Typography, IconButton, Paper, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { API_KEY } from 'src/utils/pnd/PndConstants';
import mapStyle from 'src/containers/PnD/RiderLiveMap/mapStyle';

const AllOrdersMapModal = ({ show, toggle, orders }) => {
    const { isLoaded } = useJsApiLoader({ id: 'google-map-script', googleMapsApiKey: API_KEY });
    const [selectedMarkerOrder, setSelectedMarkerOrder] = useState(null);

    const parseCoordString = (str) => {
        if (typeof str !== 'string' || !str.includes(',')) return null;
        const [lat, lng] = str.split(',').map(s => parseFloat(s.trim()));
        return isFinite(lat) && isFinite(lng) ? { latitude: lat, longitude: lng } : null;
    };

    // Process orders to extract valid dropoff locations
    const mapPoints = useMemo(() => {
        if (!orders || !orders.length) return [];
        return orders.map(order => {
            const dropoffCoords = parseCoordString(order.dropOffCoordinates) || parseCoordString(order.dropOffCoord);
            const lat = parseFloat(dropoffCoords?.latitude || order.toLatitude || order.deliveryLatitude || order.latitude || order.lat);
            const lng = parseFloat(dropoffCoords?.longitude || order.toLongitude || order.deliveryLongitude || order.longitude || order.lng);
            
            if (isFinite(lat) && isFinite(lng) && lat !== 0 && lng !== 0) {
                return {
                    order,
                    position: { lat, lng },
                    address: order.dropOffAddress || order.toName,
                    orderNumber: order.orderNumber,
                    erpOrderNo: order.erpOrderNo,
                    status: order.orderStatus,
                    toName: order.toName
                };
            }
            return null;
        }).filter(Boolean);
    }, [orders]);

    const center = useMemo(() => {
        if (mapPoints.length > 0) {
            let totalLat = 0;
            let totalLng = 0;
            mapPoints.forEach(p => {
                totalLat += p.position.lat;
                totalLng += p.position.lng;
            });
            return {
                lat: totalLat / mapPoints.length,
                lng: totalLng / mapPoints.length
            };
        }
        return { lat: 25.276987, lng: 55.296249 }; // Default Dubai
    }, [mapPoints]);

    if (!show) return null;

    return (
        <Modal open={show} onClose={toggle} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Paper elevation={24} sx={{ width: '90%', maxWidth: 1000, borderRadius: 4, overflow: 'hidden', bgcolor: 'background.paper', outline: 'none' }}>
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                        Delivery Locations Map ({mapPoints.length} Orders Mapped)
                    </Typography>
                    <IconButton size="small" onClick={toggle} sx={{ color: '#64748b' }}><CloseIcon /></IconButton>
                </Box>

                <Box sx={{ p: 0, height: { xs: 500, md: 700 }, width: '100%', position: 'relative' }}>
                    {!isLoaded ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <GoogleMap
                            mapContainerStyle={{ width: '100%', height: '100%' }}
                            center={center}
                            zoom={12}
                            options={{ styles: mapStyle }}
                        >
                            {mapPoints.map((point, index) => (
                                <Marker
                                    key={index}
                                    position={point.position}
                                    onClick={() => setSelectedMarkerOrder(point)}
                                    icon="https://maps.google.com/mapfiles/ms/icons/red-dot.png"
                                />
                            ))}

                            {selectedMarkerOrder && (
                                <InfoWindow
                                    position={selectedMarkerOrder.position}
                                    onCloseClick={() => setSelectedMarkerOrder(null)}
                                >
                                    <Box sx={{ p: 1, maxWidth: 240 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                            Order #{selectedMarkerOrder.orderNumber}
                                        </Typography>
                                        {selectedMarkerOrder.erpOrderNo && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                ERP: {selectedMarkerOrder.erpOrderNo}
                                            </Typography>
                                        )}
                                        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                                            To: {selectedMarkerOrder.toName || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1 }}>
                                            Address: {selectedMarkerOrder.address}
                                        </Typography>
                                        <Typography variant="caption" sx={{
                                            display: 'inline-block',
                                            bgcolor: 'primary.light',
                                            color: 'primary.dark',
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: 1,
                                            fontWeight: 'bold'
                                        }}>
                                            {selectedMarkerOrder.status}
                                        </Typography>
                                    </Box>
                                </InfoWindow>
                            )}
                        </GoogleMap>
                    )}
                </Box>
            </Paper>
        </Modal>
    );
};

export default AllOrdersMapModal;
